import React from 'react'
import { graphql, Link, navigate } from 'gatsby'
import PageWrap from '../components/Layouts/PageWrap'
import Offset from '../components/Layouts/Offset'
import SectionContainer from '../components/Layouts/SectionContainer'
import SectionHeader from '../components/Headers/SectionHeader'
import CardFeature from '../components/Cards/CardFeature'
import CardGroup from '../components/Cards/CardGroup'
import ButtonPrimary from '../components/Links/ButtonPrimary'
import FilterDropdown from '../components/FilterDropdown'
import { transformData as transformCardData } from '../components/Cards/Card'
import { getImageBySize } from '../components/Images/Image'

const getSeoData = (data, location) => {
  const seo = data?.archiveTemplatePage?.seo
  if (!seo) return null
  if (seo.breadcrumbs) seo.breadcrumbs = null
  seo.canonical = location.pathname
  seo.opengraphUrl = location.href
  return seo
}

const ArchivePosts = ({ data, pageContext, location }) => {
  let seo = getSeoData(data, location)
  data?.defaults && seo && (seo.defaults = data.defaults)

  // Set the published and modified times to match the most recent article
  let modified = 0
  data.page.posts.forEach(idx => {
    let currModified = Date.parse(idx.post.modified)
    modified = currModified > modified ? currModified : modified
  })
  modified = new Date(modified)

  seo.opengraphModifiedTime = modified.toISOString()
  seo.opengraphPublishedTime = modified.toISOString()

  // Grab featured post from acf field, or if not set use latest post
  const featuredPost =
    data.archiveTemplatePage.acfInsights.featuredPost ||
    data.page.posts.shift().post

  // filter out featuredPost from posts array if we are on page 2+
  const posts = data.page.posts
    .filter(({ post }) => {
      if (data.page.pageInfo.currentPage > 1) return true
      return post.id !== featuredPost.id
    })
    .map(({ post }) => transformCardData(post))

  const categories = [
    { text: 'All', value: pageContext.archivePagePath },
    ...data.categories.edges.map(({ node }) => ({
      text: node.name,
      value: node.uri,
    })),
  ]

  return (
    <PageWrap seo={seo}>
      <div className="pb-40">
        <SectionContainer>
          <div className="container relative z-10">
            <SectionHeader
              heading="Insights"
              subheading="Insights and tips from our experts"
              as="h1"
            />
            <div className="my-8">
              <FilterDropdown
                items={categories}
                onItemSelect={({ selectedItem }) =>
                  navigate(selectedItem.value)
                }
                initialSelectedItem={categories[0]}
              />
            </div>
          </div>
          {data.page.pageInfo.currentPage === 1 && (
            <Offset rounded={false}>
              <CardFeature
                {...transformCardData(featuredPost)}
                image={
                  featuredPost.featuredImage &&
                  getImageBySize(
                    featuredPost.featuredImage.node,
                    'medium_large'
                  )
                }
              />
            </Offset>
          )}
        </SectionContainer>
        <div className="container">
          <CardGroup cards={posts} />
          {(pageContext.previousPagePath || pageContext.nextPagePath) && (
            <div className="text-center mt-10 flex flex-col md:flex-row justify-center">
              {pageContext.previousPagePath && (
                <div className="mt-10 md:px-4">
                  <ButtonPrimary as={Link} to={pageContext.previousPagePath}>
                    Previous Page
                  </ButtonPrimary>
                </div>
              )}
              {pageContext.nextPagePath && (
                <div className="mt-10 md:px-4">
                  <ButtonPrimary as={Link} to={pageContext.nextPagePath}>
                    Next Page
                  </ButtonPrimary>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </PageWrap>
  )
}

export default ArchivePosts

export const query = graphql`
         query($limit: Int, $skip: Int) {
           categories: allWpCategory(filter: { count: { gt: 0 } }) {
             edges {
               node {
                 name
                 uri
               }
             }
           }
           page: allWpPost(
             limit: $limit
             skip: $skip
             sort: { fields: date, order: DESC }
           ) {
             pageInfo {
               itemCount
               pageCount
               currentPage
             }
             posts: edges {
               post: node {
                 ...postCardFields
               }
             }
           }
           modified: allWpPost(limit: 1) {
             edges {
               node {
                 modified
               }
             }
           }
           defaults: wp {
             insightOptions {
               acfArchivePageOptions {
                 defaultOgImage {
                   link
                   sourceUrl
                   mediaDetails {
                     sizes {
                       name
                       sourceUrl
                     }
                   }
                 }
               }
             }
           }
           archiveTemplatePage: wpPage(pageTemplate: { eq: "PostAchive.js" }) {
             id
             seo {
               ...seoPostTypeFields
             }
             acfInsights {
               featuredPost {
                 ... on WpPost {
                   ...postCardFields
                 }
               }
             }
           }
         }
       `
